import { useEffect, useState } from "react";
import { useCurrency } from "@/lib/CurrencyContext";
import { ChargedInAed } from "@/components/CurrencySelector";
import { motion, AnimatePresence } from "framer-motion";
import { X, Trash2, ShoppingBag, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCart } from "@/lib/CartContext";
import { useAuth } from "@/lib/AuthContext";
import { useLocale } from "@/lib/LocaleContext";
import { localisedTo } from "@/lib/i18nRoutes";
import { api, ASSET_BASE, ASSETS } from "@/lib/api";
import SecurePayments from "@/components/SecurePayments";
import TermsBlock from "@/components/TermsBlock";
import { formatTrackTitle } from "@/lib/trackTitle";
import { normalizeCheckoutError } from "@/lib/checkoutError";
import { useVerificationGate } from "@/lib/VerificationGate";
import { useShoppingSession } from "@/lib/ShoppingSession";
import { fireSnapStartCheckout } from "@/components/TrackingLoader";


export default function CartDrawer() {
  const { format: fmt, active, converted } = useCurrency();
  const { items, packs, isOpen, close, removeItem, removeMany, removePack, clear } = useCart();
  const { user } = useAuth();
  const { t } = useTranslation();
  const { lang, isRTL } = useLocale();
  const navigate = useNavigate();
  const { openVerificationGate } = useVerificationGate();
  const { termsAccepted, nudgeTerms, policyVersion } = useShoppingSession();
  const [checkingOut, setCheckingOut] = useState(false);
  const [quote, setQuote] = useState(null);        // server-authoritative
  const [quoteBusy, setQuoteBusy] = useState(false);

  // Server-authoritative cart quote.  Re-fetched whenever the cart
  // membership changes.  Displayed values (subtotal, discount, total,
  // promotion name) are read from this quote, NOT re-derived in JS.
  useEffect(() => {
    if (!isOpen) return;
    const ids = items.map((x) => x.id);
    const packIds = packs.map((x) => x.id);
    if (ids.length === 0 && packIds.length === 0) { setQuote(null); return; }
    let alive = true;
    setQuoteBusy(true);
    api.post("/cart/quote", { track_ids: ids, pack_ids: packIds })
      .then((r) => {
        if (!alive) return;
        setQuote(r.data);
        const dups = r.data?.removed_duplicate_ids || [];
        if (dups.length) {
          removeMany(dups);
          toast(t("packs.duplicateRemoved", { count: dups.length }));
        }
        (r.data?.invalid_pack_ids || []).forEach((id) => removePack(id));
      })
      .catch(() => { if (alive) setQuote(null); })
      .finally(() => { if (alive) setQuoteBusy(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, items.map((x) => x.id).join(","), packs.map((x) => x.id).join(",")]);

  // Close on ESC + lock body scroll while open
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [isOpen, close]);

  const subtotal = items.reduce((s, x) => s + Number(x.price || 0), 0);

  // Server-authoritative promotion values (fall back to raw subtotal
  // while the quote is still loading — final total is always confirmed
  // by the server at checkout time).
  const promoName = quote?.promotion_applied || null;
  const discountAmount = Number(quote?.discount_amount || 0);
  const discountPct = Number(quote?.promotion_discount_percent || 0);
  const finalTotal = quote ? Number(quote.final_total || 0) : subtotal;
  const promoActive = Boolean(promoName) && discountAmount > 0;
  const packLines = quote?.pack_lines || [];
  const packsSavings = Number(quote?.packs_savings || 0);
  const showBreakdown = promoActive || packsSavings > 0;
  const isEmpty = items.length === 0 && packs.length === 0;
  const lineFor = (id) => packLines.find((l) => l.pack_id === id);
  const checkoutCount = items.length + packs.reduce((n, p) => n + (lineFor(p.id)?.payable_track_ids?.length ?? p.track_count ?? 0), 0);
  const promoLabel = promoName === "all_premium"
    ? t("promotions.strip.bundle.tag", { defaultValue: "ALL PREMIUM" })
    : promoName === "five_for_five"
      ? t("promotions.strip.fiveForFive.tag", { defaultValue: "5 FOR 5" })
      : "";

  const onCheckout = async () => {
    // Double-tap / rerender guard — the button's disabled attribute is
    // set from the same state but React can dispatch the click before
    // React commits the disabled bit on mobile Safari. Return early
    // here so we never open two Stripe Checkout Sessions for one cart.
    if (checkingOut) return;
    if (isEmpty) return;
    if (!user) {
      close();
      toast(t("cart.signInToCheckout"), { description: t("cart.signInDescription") });
      navigate(localisedTo("/login?next=/#music", lang));
      return;
    }
    if (!termsAccepted) {
      nudgeTerms();
      return;
    }
    setCheckingOut(true);
    try {
      const { data } = await api.post("/checkout/cart", {
        track_ids: items.map((x) => x.id),
        pack_ids: packs.map((x) => x.id),
        origin_url: window.location.origin,
        terms_accepted: true,
        policy_version: policyVersion,
      });
      if (data.url) {
        // Fire Snapchat START_CHECKOUT once the server confirms a
        // valid Stripe session — never on button click.  Value comes
        // from the server-authoritative `data.total` (the same final
        // AED amount sent to Stripe after promotions/discounts).
        try {
          fireSnapStartCheckout({
            value: Number(data.total ?? quote?.total_aed ?? 0),
            currency: (data.currency || "AED").toUpperCase(),
            productIds: items.map((x) => x.id),
            numItems: data.track_count ?? items.length,
          });
        } catch { /* noop */ }
        // Stripe checkout — cart survives until purchase confirmed on success page
        window.location.href = data.url;
      }
    } catch (err) {
      const norm = normalizeCheckoutError(err, t("cart.checkoutFailed"));
      if (norm.isEmailNotVerified) {
        // Close the drawer so the modal isn't visually stacked on it;
        // the cart is preserved in localStorage via CartContext.
        close();
        openVerificationGate({ email: user?.email });
      } else {
        toast.error(norm.message);
      }
    } finally { setCheckingOut(false); }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.button
            type="button"
            aria-label="Close cart"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/75 backdrop-blur-sm z-[100]"
            onClick={close}
            data-testid="cart-backdrop"
          />
          <motion.aside
            initial={{ x: isRTL ? "-100%" : "100%" }}
            animate={{ x: 0 }}
            exit={{ x: isRTL ? "-100%" : "100%" }}
            transition={{ type: "tween", duration: 0.28 }}
            className={`fixed top-0 h-full w-full sm:w-[420px] bg-[#050505] z-[101] flex flex-col ${
              isRTL ? "left-0 border-r border-white/10" : "right-0 border-l border-white/10"
            }`}
            data-testid="cart-drawer"
          >
            <div className="p-5 border-b border-white/10 flex items-center justify-between">
              <div>
                <p className="font-mono text-[10px] tracking-[0.3em] text-red-brand">{t("cart.kicker")}</p>
                <h3 className="font-anton text-2xl mt-1">{t("cart.title")}</h3>
              </div>
              <button
                onClick={close}
                data-testid="cart-close"
                className="text-white/60 hover:text-white p-2"
                aria-label={t("cart.close")}
              >
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5" data-testid="cart-items">
              {isEmpty ? (
                <div className="text-white/50 text-sm text-center py-16">
                  <ShoppingBag size={40} className="mx-auto text-white/20 mb-3" />
                  <div>{t("cart.empty")}</div>
                  <div className="mt-1 text-xs text-white/40">{t("cart.emptyHint")}</div>
                </div>
              ) : (
                <ul className="space-y-3">
                  {packs.map((pk) => {
                    const ln = lineFor(pk.id);
                    const name = (lang === "ar" && pk.name_ar) ? pk.name_ar : pk.name_en;
                    return (
                      <li key={`pack-${pk.id}`} data-testid={`cart-pack-${pk.id}`}
                          className="rounded-xl p-3 bg-red-brand/[0.06] border border-red-brand/30 flex items-center gap-3">
                        <img src={pk.artwork_url ? `${ASSET_BASE}${pk.artwork_url}&size=thumb` : ASSETS.logoCover} alt=""
                             className="w-14 h-14 rounded-lg object-cover" />
                        <div className="flex-1 min-w-0">
                          <div className="font-mono text-[9px] tracking-[0.3em] text-red-brand">{t("packs.cartPackLabel")}</div>
                          <div className="font-display text-base truncate">{name}</div>
                          <div className="font-mono text-[10px] text-white/50 tracking-widest" dir="ltr" data-testid={`cart-pack-price-${pk.id}`}>
                            {ln ? <>{t("packs.tracks", { count: ln.payable_track_ids.length })} · <s className="text-white/30">{fmt(ln.original)}</s> {fmt(ln.price)}</> : "…"}
                          </div>
                        </div>
                        <button onClick={() => removePack(pk.id)} data-testid={`cart-pack-remove-${pk.id}`}
                                className="text-white/40 hover:text-red-brand p-2" aria-label={t("cart.remove", { title: name })}>
                          <Trash2 size={16} />
                        </button>
                      </li>
                    );
                  })}
                  {items.map((it) => (
                    <li
                      key={it.id}
                      data-testid={`cart-item-${it.id}`}
                      className="hairline rounded-xl p-3 bg-white/[0.02] flex items-center gap-3"
                    >
                      <img
                        src={it.cover_url ? `${ASSET_BASE}${it.cover_url}` : ASSETS.logoCover}
                        alt=""
                        className="w-14 h-14 rounded-lg object-cover"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-display text-base truncate">{formatTrackTitle(it)}</div>
                        <div className="font-mono text-[10px] text-white/50 tracking-widest" dir="ltr">
                          {it.genre?.toUpperCase()} · {fmt(it.price)}
                        </div>
                      </div>
                      <button
                        onClick={() => removeItem(it.id)}
                        data-testid={`cart-remove-${it.id}`}
                        className="text-white/40 hover:text-red-brand p-2"
                        aria-label={t("cart.remove", { title: formatTrackTitle(it) })}
                      >
                        <Trash2 size={16} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {!isEmpty && (
              <div className="p-5 border-t border-white/10 bg-black/40">
                <div className="mb-3">
                  <TermsBlock variant="cart" testId="cart-terms-checkbox" />
                </div>

                {/* Promotion strip — server-authoritative.  Renders only
                    the actual promotion the backend will charge with. */}
                {promoActive && (
                  <div
                    data-testid="cart-promo-strip"
                    className="mb-3 rounded-xl px-3 py-2 text-xs bg-red-brand/15 border border-red-brand/40 text-white"
                  >
                    <div className="font-mono tracking-widest uppercase text-[10px] mb-1 text-red-brand"
                         data-testid={`cart-promo-name-${promoName}`}>
                      {promoLabel} · {discountPct}% OFF
                    </div>
                    <div data-testid="cart-promo-unlocked">
                      {promoName === "all_premium"
                        ? t("promotions.bundle.unlocked", {
                            defaultValue: "Complete Premium collection — {{percent}}% off applied",
                            percent: discountPct,
                          })
                        : t("promotions.fiveForFive.unlocked", { percent: discountPct })}
                    </div>
                  </div>
                )}

                {packLines.length > 0 && (promoName) && (
                  <p className="mb-3 text-[11px] text-white/50" data-testid="cart-pack-no-stack">{t("packs.noPromoStack")}</p>
                )}

                {showBreakdown ? (
                  <div className="mb-4 space-y-1.5" data-testid="cart-totals-discounted">
                    <div className="flex items-center justify-between font-mono text-[11px] tracking-[0.2em] text-white/60">
                      <span>{t("cart.subtotal")}</span>
                      <span dir="ltr">{fmt(quote?.subtotal ?? subtotal)}</span>
                    </div>
                    {packsSavings > 0 && (
                      <div className="flex items-center justify-between font-mono text-[11px] tracking-[0.2em] text-red-brand" data-testid="cart-pack-savings-line">
                        <span>{t("packs.cartPackSaved")}</span>
                        <span dir="ltr">−{fmt(packsSavings)}</span>
                      </div>
                    )}
                    {promoActive && (
                      <div className="flex items-center justify-between font-mono text-[11px] tracking-[0.2em] text-red-brand" data-testid="cart-discount-line">
                        <span>{t("cart.discount")}</span>
                        <span dir="ltr">−{fmt(discountAmount)}</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between pt-1 border-t border-white/10">
                      <span className="font-mono text-[10px] tracking-[0.3em] text-white/50" data-testid="cart-total-label">{converted ? t("currency.estTotal", { currency: active }) : t("cart.total")}</span>
                      <span data-testid="cart-total" className="font-anton text-3xl text-white" dir="ltr">{fmt(finalTotal)}</span>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between mb-4">
                    <span className="font-mono text-[10px] tracking-[0.3em] text-white/50" data-testid="cart-total-label">{converted ? t("currency.estTotal", { currency: active }) : t("cart.total")}</span>
                    <span data-testid="cart-total" className="font-anton text-3xl text-white" dir="ltr">
                      {quoteBusy ? "…" : fmt(finalTotal)}
                    </span>
                  </div>
                )}

                <ChargedInAed aedTotal={finalTotal} testid="cart-charged-in-aed" className="mb-3 rounded-lg border border-red-brand/40 bg-red-brand/[0.06] px-3 py-2" prominent />

                <button
                  onClick={onCheckout}
                  disabled={checkingOut}
                  data-testid="cart-checkout"
                  className="btn-primary w-full justify-center"
                >
                  {checkingOut ? <Loader2 size={16} className="animate-spin" /> : <ShoppingBag size={16} />}
                  {checkingOut ? t("cart.redirecting") : t("cart.checkoutN", { count: checkoutCount })}
                </button>
                <div className="mt-3">
                  <SecurePayments variant="compact" />
                </div>
                <button
                  onClick={() => { if (window.confirm(t("cart.clearConfirm"))) clear(); }}
                  data-testid="cart-clear"
                  className="text-[11px] font-mono text-white/40 hover:text-red-brand mt-3 tracking-widest w-full text-center"
                >
                  {t("cart.clear")}
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
